<template>
  <div class="audio-form opc-zmaudio__form-item">
    <!--header-->
    <div class="audio-form__header">
      <h1>{{ title }}</h1>
      <zm-button data-testid="checkout-button-74" icon="zm-icon-close" ghost v-if="canRemove" @click="handleRemove"></zm-button>
    </div>
    <p class="audio-form__sub-title" @click="onSubmitFormClick" v-html="desc"/>
    <cycle-radio-group
      :value="value.billing_cycle"
      :plans="audioPlans"
      :action="switchBillingCycle"
      aria-label="Zoom Audio Conferencing Plan"
      class="audio-form__radio-group opc-card-radio">
      <zm-radio
        v-for="item in radioData"
        data-testid="checkout-radio-75"
        :key="item.billing_cycle"
        :label="item.billing_cycle"
        :disabled="calculateIsOptionDisabled(item.planData)"
        class="audio-form__radio">
        <p class="audio-form__radio-price-row">{{ item.title }}</p>
        <div class="audio-form__radio-amount-row">
          <p class="audio-form__radio-amount-row-amount">
            {{ item.amountText }}
          </p>
          <p class="audio-form__radio-amount-row-unit" v-if="item.billing_cycle !== BILLING_CYCLE.PAY_AS_YOU_GO">
            {{ item.unit }}<priceVATTip />
          </p>
        </div>
        <p class="audio-form__radio-desc-row">{{ item.desc }}</p>
      </zm-radio>
    </cycle-radio-group>
    <div class="audio-form__countries-regions">
      <h2 class="audio-form__countries-regions-title">{{ $t('billing.tollfree.enable_countries_regions') }}</h2>
      <p class="audio-form__countries-regions-desc">{{ $t('billing.tollfree.enable_countries_regions_desc') }}</p>
      <zm-alert
        v-if="regionErrMsg"
        :title="regionErrMsg"
        type="error"
        class="audio-form__error-message"
        show-icon
        :closable="false"
      />
      <CountriesRegionsTable
        :key="regionTableKey"
        :rate-type="rateType"
        :bundle-type="bundleType"
        :selections="rateSelections"
        @selection-change="handleSelectionChange"
      />
      <preview-payment class="audio-form__preview-payment" />
    </div>
  </div>
</template>
<script>
import CycleRadioGroup from '@/components/service/cycle-radio-group';
import { mapActions, mapGetters } from 'vuex';
import {
  BILLING_CYCLE,
  planTypes,
  AUDIO_RATE_TYPES,
  AUDIO_BUNDLE_TYPE,
  AUDIO_SUB_TYPES,
  A_LINK_TAG_NAME,
  MRS_STATUS_TYPE_MAP
} from '@/common/constants';
import computedMixins from '@/plans/TOLLFREE/mixins/computed-mixins';
import CountriesRegionsTable from '@/plans/TOLLFREE/components/countries-regions-table/index.vue';
import { get } from 'lodash';
import { isOnlineNotSupportPlan } from "@/common/service/non-standard-billing-cycle-utils";
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import { checkMRSChange } from '@/utils/validator';

export default {
  name: 'AudioInternationalForm',
  mixins: [computedMixins],
  data() {
    return {
      rateSelections: {
        [AUDIO_RATE_TYPES.CALL_OUT]: [],
        [AUDIO_RATE_TYPES.TOLL_FREE]: [],
        [AUDIO_RATE_TYPES.PREMIUM]: [],
      },
      tableRefreshToken: 0,
      BILLING_CYCLE
    };
  },
  components: {
    PreviewPayment,
    CycleRadioGroup,
    CountriesRegionsTable,
  },
  props: {
    title: {
      type: String,
      require: true,
    },
    desc: {
      type: String,
      require: true,
    },
  },
  computed: {
    ...mapGetters(['getCurrentBasePlanInfo', 'currentEditPlan', 'basicConfig']),
    audioPlans() {
      return this.audioPlansFilter(this.planMapFlat);
    },
    nonStandardSelectedAudioPlans() {
      return this.audioPlans.filter(
        plan => isOnlineNotSupportPlan({ planType: planTypes.TOLLFREE, billingCycle: plan.billing_cycle, category: plan.category }) && plan?.cartInfo?.selected_number > 0
      )
    },
    standardAudioPlans() {
      return this.audioPlans.filter(
        plan => !isOnlineNotSupportPlan({ planType: planTypes.TOLLFREE, billingCycle: plan.billing_cycle, category: plan.category })
      )
    },
    radioData() {
      const monthlyPlan = this.audioPlans.find(item => item.billing_cycle === BILLING_CYCLE.MONTHLY);
      const monthlyAmount = monthlyPlan?.amount || 0;
      const descMap = {
        [BILLING_CYCLE.MONTHLY]: this.$t('billing.tollfree.audio_conferencing.montly_desc', [this.$money(monthlyAmount)]),
        [BILLING_CYCLE.PAY_AS_YOU_GO]: this.$t('billing.tollfree.audio_conferencing.pay_you_go_desc'),
      }
      if (this.nonStandardSelectedAudioPlans.length > 0) {
        return this.nonStandardSelectedAudioPlans.map(item => ({
          title: this.$t(`billing.cycle.${item.billing_cycle}`),
          amountText: this.$money(item.amount, { forceDiff: true }),
          unit: this.$t(`billing.common.per_billing_cycle.${item.billing_cycle}`),
          billing_cycle: item.billing_cycle,
          desc: '',
          planData: item,
        }))
      }
      return this.standardAudioPlans.map(item => ({
        title: this.$t(`billing.cycle.monthly`),
        amountText: item.amount ? this.$money(item.amount, { forceDiff: true }) : this.$t('billing.pay_you_go'),
        unit: this.$t(`billing.common.per_billing_cycle.${item.billing_cycle}`),
        billing_cycle: item.billing_cycle,
        desc: this.mx_showAudioPayYouGo ? descMap[item.billing_cycle] : '',
        planData: item,
      }));
    },
    value() {
      return this.audioPlansFilter(this.getSelectedPlans)[0];
    },
    rateType() {
      if(this.value.plan_code) {
        return this.value.plan_code + '_audio_rates';
      }
      return 'tollfree_monthly_commitment_100_audio_rates';
    },
    bundleType() {
      if (this.mx_hasUnlimitedPlan) {
        return AUDIO_BUNDLE_TYPE.US_CA;
      }
      return AUDIO_BUNDLE_TYPE.NONE;
    },
    regionTableKey() {
      return this.value.plan_code + this.tableRefreshToken
    },
    canRemove() {
      return this.mx_selectedCategories.length > 1 && this.mx_newCategorys.includes(AUDIO_SUB_TYPES.TOLL_FREE);
    },
    regionErrMsg() {
      const tollFreeProfileData = this.value.cartInfo.tollFreeProfileData;
      const rateSelections = {
        [AUDIO_RATE_TYPES.CALL_OUT]: tollFreeProfileData[[AUDIO_RATE_TYPES.CALL_OUT]].split(',').filter(Boolean),
        [AUDIO_RATE_TYPES.TOLL_FREE]: tollFreeProfileData[[AUDIO_RATE_TYPES.TOLL_FREE]].split(',').filter(Boolean),
        [AUDIO_RATE_TYPES.PREMIUM]: tollFreeProfileData[[AUDIO_RATE_TYPES.PREMIUM]].split(',').filter(Boolean),
      };
      if(
        this.getCurrentBasePlanInfo.planType === planTypes.VIDEO_SDK &&
        rateSelections[AUDIO_RATE_TYPES.CALL_OUT].length <= 0
      ) {
        return this.$t('billing.tollfree.select_at_least_one_country');
      }
      if(
        rateSelections[AUDIO_RATE_TYPES.TOLL_FREE].length <= 0 &&
        rateSelections[AUDIO_RATE_TYPES.PREMIUM].length <= 0 &&
        rateSelections[AUDIO_RATE_TYPES.CALL_OUT].length <= 0
      ) {
        return this.$t('billing.tollfree.select_at_least_one_country');
      }
      return ''
    },
    disableDownSell() {
      return this.basicConfig.downSellData.disableDownSell;
    },
  },
  created() {
    this.initTollFreeProfileData();
    this.updateSelectionData();
  },
  methods: {
    ...mapActions(['switchBillingCycleAction']),
    initTollFreeProfileData() {
      if(!this.value.cartInfo.tollFreeProfileData) {
        this.mx_addAudioPlan(this.value.plan_code, this.value.billing_cycle);
      }
    },
    updateSelectionData() {
      const tollFreeProfileData = this.value.cartInfo.tollFreeProfileData;
      if(tollFreeProfileData) {
        this.rateSelections = {
          [AUDIO_RATE_TYPES.CALL_OUT]: tollFreeProfileData[[AUDIO_RATE_TYPES.CALL_OUT]].split(',').filter(Boolean),
          [AUDIO_RATE_TYPES.TOLL_FREE]: tollFreeProfileData[[AUDIO_RATE_TYPES.TOLL_FREE]].split(',').filter(Boolean),
          [AUDIO_RATE_TYPES.PREMIUM]: tollFreeProfileData[[AUDIO_RATE_TYPES.PREMIUM]].split(',').filter(Boolean),
        };
      }
      this.tableRefreshToken++;
    },
    audioPlansFilter(data) {
      return data
      .filter(item => item.type === planTypes.TOLLFREE)
      .filter(item => item.category === 'tollfree')
      // Audio Pay As You Go only show when base plan is biz
      .filter(item => !(!this.mx_showAudioPayYouGo && item.billing_cycle === BILLING_CYCLE.PAY_AS_YOU_GO) );
    },
    async switchBillingCycle(oldPlan, newPlan) {
      if (!this.value) {
        return;
      }

      const cartInfo = { ...this.value.cartInfo };
      const nextPlan = await this.switchBillingCycleAction([oldPlan?.plan_code, {
        ...newPlan,
        cartInfo,
      }]);

      this.$emit('change', nextPlan)

      // event tracking
      const billingCycle = newPlan?.billing_cycle;
      const _newRadio = this.radioData.find(item => item.billing_cycle === billingCycle);
      billingCycle && this.$tracking.buyInteraction(
        `Audio Conferencing Plan`,
        _newRadio.title?.toLowerCase(),
      );
      this.updateSelectionData();
    },
    handleSelectionChange({ type, countryIds }) {
      const oldTollfreeProfileData = this.value.cartInfo.tollFreeProfileData;
      const rateSelections = {
        ...oldTollfreeProfileData,
        [type]: countryIds.join(','),
      }
      if(this.value) {
        this.updateSelectPlan({
          plan_code: this.value.plan_code,
          planType: planTypes.TOLLFREE,
          billing_cycle: this.value.billing_cycle,
          category: this.value.category,
          cartInfo: {
            ...this.value.cartInfo,
            tollFreeProfileData: rateSelections,
          },
        });
      }
    },
    handleRemove() {
      this.$emit('remove', AUDIO_SUB_TYPES.TOLL_FREE);
    },
    onSubmitFormClick(event) {
      const basePlanNameKey = get(this.getCurrentBasePlanInfo, 'plans[0].planNameKey', '');
      if (event?.target?.tagName === A_LINK_TAG_NAME) {
        const text = event?.target?.innerText;
        this.$tracking.buyInteraction(
          `${this.$t(basePlanNameKey)} add-ons`,
          `Audio Conferencing - ${text}`);
      }
    },
    calculateIsOptionDisabled(plan) {
      if (this.disableDownSell) {
        return checkMRSChange({
          oldPlan: this.currentEditPlan,
          nextPlan: plan,
          nextPlanQuantity: 1
        }) === MRS_STATUS_TYPE_MAP.CHANGE_FUTURE
      }
      return false
    }
  },
  watch: {
    mx_hasUnlimitedPlan(value, oldValue) {
      if(value && !oldValue) {
        this.$nextTick(this.updateSelectionData.bind(this))
      }
    }
  },
}
</script>
<style lang="scss">
@import '@/styles/scss-utils.scss';
.audio-form {
  &__header {
    display: flex;
    h1 {
      color: var(--day-foreground-foreground-strong, #00031F);
      @include darkTextColor;
      /* Title/Title S */
      font-family: Almaden Sans;
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 150%; /* 27px */
      flex: 1;
    }
  }
  &__sub-title {
    color: var(--neutral-neutral-08, #515079);
    @include darkTextColor;

    /* body/LF — S (R) */
    font-family: Almaden Sans;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 21px */
  }

  &__radio-group {
    margin-top: 8px;
    &.opc-card-radio {
      width: 100%;
      flex-direction: column;
    }
  }
  &__radio {
    &.zm-radio {
      width: 100%;
      .zm-radio__wrap {
        width: 100%;
      }
      .zm-radio__label {
        width: 100%;
      }
      .zm-radio__label--inner {
        width: 100%;
      }
    }
    &-price-row {
      color: var(--neutral-midnight, #00053D);
      @include darkTextColor;
      /* Label/Label L */
      font-family: Almaden Sans;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 150%; /* 24px */
    }
    &-amount-row {
      /* Title/Title L */
      font-family: Almaden Sans;
      font-style: normal;
      word-break: break-word;
      &-amount {
        color: var(--neutral-midnight, #00053D);
        @include darkTextColor;
        font-size: 22px;
        font-weight: 700;
        line-height: 150%; /* 33px */
      }
      &-unit {
        color: var(--neutral-neutral-08, #515079);
        @include darkTextColor2;
        font-size: 12px;
        font-weight: 500;
        line-height: 150%; /* 18px */
      }
    }
    &-desc-row {
      color: var(--neutral-neutral-08, #515079);
      @include darkTextColor2;
      /* body/LF — S (R) */
      font-family: Almaden Sans;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%; /* 21px */
    }
  }

  &__countries-regions {
    margin-top: 24px;
    &-title {
      color: var(--day-foreground-foreground-strong, #00031F);
      @include darkTextColor;

      /* Title/Title S */
      font-family: Almaden Sans;
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 150%; /* 27px */
      margin-bottom: 4px;
    }
    &-desc {
      color: var(--neutral-neutral-08, #515079);
      @include darkTextColor2;

      /* body/LF — S (R) */
      font-family: Almaden Sans;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%; /* 21px */
      margin-bottom: 8px;
    }
  }
  &__error-message.zm-alert.zm-alert--error {
    border-radius: 8px;
    background: var(--extended-rose-20, #F3C2D0);
    padding: 8px 12px;
    margin-bottom: 8px;
    .zm-alert__title {
      color: var(--extended-rose-80, #80384D);

      /* Body/Body S */
      font-family: Almaden Sans;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%; /* 21px */
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
</style>
